import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { BoundingBox } from "../../core/mdl/BoundingBox";
import { LengthFeedback } from "../../core/tool/LengthFeedback";
import { SnapGrid } from "../../core/tool/SnapGrid";
import { SnapLine } from "../../core/tool/SnapLine";
import { SnapPoint } from "../../core/tool/SnapPoint";
import { projectPointOnPlane, VECTOR3_X, VECTOR3_Y, VECTOR3_Z } from "../../core/utils/MathUtils";
import { resizeInViewSpace } from "../../core/utils/MeshUtils";
import { CoordinatesType, ViewLayerMaskFlags } from "../../core/view/View";
import { EPSILON, eq } from "../../utils/math";
import { AbstractMove } from "./AbstractMove";
/**
 * DefaultMove
 */
export class DefaultMove extends AbstractMove {
    constructor(pApp) {
        super(pApp);
        this.onCameraChanged = () => {
            if (this.mApp.settings.snapping.gridDynamic) {
                this.mSnapGrid.updateSize(this.mLastPickPos != null ? this.mLastPickPos : this.mRefPoint, this.mView);
            }
            this.updateFeedback(null, null, false);
            this.mApp.viewer.requestRenderFrame();
        };
        this.mStartPointerId = 0;
        this.mSnapGrid = new SnapGrid();
        this.mGridAxis = new Array();
        this.mSnapAxes = new Array();
        this.mBBoxSnapDistance = 0.1;
        this.mBBoxSnapped = false;
        this.mCurrentSnapAxis = null;
        this.mAxisFeedback = null;
        this.mSnapAxisFeedback = null;
        this.mRefPointFeedback = null;
        this.mRefPointFeedbackMat = null;
        this.mBBoxSnapPoints = null;
        this.mBBoxSnapPointsWorld = null;
        this.mElementSnapData = null;
    }
    start(pParameter) {
        const tResult = super.start(pParameter);
        if (!tResult)
            return (tResult);
        this.mCurrentSnapAxis = null;
        this.mBBoxSnapped = false;
        this.mView.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
        this.prepareFeedback();
        return (true);
    }
    stop() {
        this.clearFeedback();
        this.mView.cameraControl.eventCameraChanged.removeListener(this.onCameraChanged);
        super.stop();
    }
    handleKeyUp(pEvent, pState) {
        //shift -> lock snap axis
        if (pEvent.which === 16 && this.mCurrentSnapAxis != null && !this.mMoveY)
            this.mCurrentSnapAxis.locked = !this.mCurrentSnapAxis.locked;
    }
    initialize(pRefPoint, pStartPointerId, pMoveY, pAllowMoveBelowGround, pGroundPosition) {
        this.mRefPoint = pRefPoint.clone();
        this.mMoveY = pMoveY;
        this.mStartPointerId = pStartPointerId;
        this.mAllowMoveBelowGround = pAllowMoveBelowGround;
        this.mGroundPosition = pGroundPosition;
    }
    //// protected ///////////////////////////////////////////////////////////////////////////////
    prepareSnapping() {
        this.mPickPlane = this.getPickPlane(this.mRefPoint);
        this.prepareGrid();
        this.prepareBoundsSnap();
    }
    performSnap(pPos, pEvent, pState) {
        let tCurrentPos = this.snapOnAxis(pPos);
        tCurrentPos = this.snapOnBounds(tCurrentPos);
        tCurrentPos = this.preventMoveBelowGround(tCurrentPos);
        if (!this.mBBoxSnapped)
            tCurrentPos = this.snapOnGrid(tCurrentPos);
        tCurrentPos = this.limitMovement(tCurrentPos);
        if (tCurrentPos != null) {
            this.updateFeedback(pState.pointers.get(pEvent.pointerId), tCurrentPos, true);
        }
        return (tCurrentPos);
    }
    prepareFeedback() {
        this.clearFeedback();
        const tScene = this.mApp.model.scene;
        this.mSnapAxes = new Array();
        this.mAxisFeedback = new Array();
        let tFeedbackNormal = this.mPickPlane.normal;
        if (this.mMoveY) {
            const tV = Vector3.Cross(VECTOR3_Y, this.mView.cameraControl.direction);
            if (!eq(tV.lengthSquared(), 0)) {
                tV.normalize();
                tFeedbackNormal = Vector3.Cross(VECTOR3_Y, tV);
            }
            //y axis
            const tFeedback = new LengthFeedback(0, this.mRefPoint, VECTOR3_Y, "#95c11f", tFeedbackNormal, tScene, this.mView);
            this.mAxisFeedback.push(tFeedback);
            const tSnap = SnapLine.FromAxis(this.mRefPoint, VECTOR3_Y);
            tSnap.locked = true;
            this.mSnapAxes.push(tSnap);
        }
        else {
            //xz plane
            if (!eq(this.mPickPlane.normal.y, 0)) {
                //x axis
                let tFeedback = new LengthFeedback(1, this.mRefPoint, VECTOR3_X, "#c00303", tFeedbackNormal, tScene, this.mView);
                this.mAxisFeedback.push(tFeedback);
                this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, VECTOR3_X));
                //z axis
                tFeedback = new LengthFeedback(0, this.mRefPoint, VECTOR3_Z, "#0084b4", tFeedbackNormal, tScene, this.mView);
                this.mAxisFeedback.push(tFeedback);
                this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, VECTOR3_Z));
            }
            else 
            //xy plane
            if (!eq(this.mPickPlane.normal.z, 0)) {
                //x axis
                let tFeedback = new LengthFeedback(1, this.mRefPoint, VECTOR3_X, "#c00303", tFeedbackNormal, tScene, this.mView);
                this.mAxisFeedback.push(tFeedback);
                this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, VECTOR3_X));
                //y axis
                tFeedback = new LengthFeedback(0, this.mRefPoint, VECTOR3_Y, "#95c11f", tFeedbackNormal, tScene, this.mView);
                this.mAxisFeedback.push(tFeedback);
                this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, VECTOR3_Y));
            }
            else 
            //zy plane
            {
                //z axis
                let tFeedback = new LengthFeedback(1, this.mRefPoint, VECTOR3_Z, "#0084b4", tFeedbackNormal, tScene, this.mView);
                this.mAxisFeedback.push(tFeedback);
                this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, VECTOR3_Z));
                //y axis
                tFeedback = new LengthFeedback(0, this.mRefPoint, VECTOR3_Y, "#95c11f", tFeedbackNormal, tScene, this.mView);
                this.mAxisFeedback.push(tFeedback);
                this.mSnapAxes.push(SnapLine.FromAxis(this.mRefPoint, VECTOR3_Y));
            }
        }
        //RefPoint Feedback
        this.mRefPointFeedbackMat = new PBRMaterial("", tScene);
        this.mRefPointFeedbackMat.albedoColor = Color3.Black();
        this.mRefPointFeedbackMat.emissiveColor = Color3.FromHexString("#868686").toLinearSpace();
        this.mRefPointFeedbackMat.disableLighting = true;
        this.mRefPointFeedbackMat.unlit = true;
        this.mRefPointFeedbackMat.backFaceCulling = false;
        this.mRefPointFeedbackMat.freeze();
        this.mRefPointFeedback = CreateDisc("", { tessellation: 8, radius: 1 }, tScene);
        this.mRefPointFeedback.renderingGroupId = 3;
        this.mRefPointFeedback.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mRefPointFeedback.isPickable = false;
        this.mRefPointFeedback.material = this.mRefPointFeedbackMat;
        this.mRefPointFeedback.position = this.mRefPoint;
        this.mRefPointFeedback.lookAt(this.mRefPoint.add(tFeedbackNormal), 0, 0, 0);
        const tPointer = this.mStartPointerId != null ? this.mApp.viewer.inputState.getPointer(this.mStartPointerId) : null;
        this.updateFeedback(tPointer, this.mRefPoint, false);
        //snap axis feedback
        this.mSnapAxisFeedback = CreateLines("", { points: [VECTOR3_Z.scale(-100), VECTOR3_Z.scale(100)], updatable: false, instance: null }, tScene);
        this.mSnapAxisFeedback.isPickable = false;
        this.mSnapAxisFeedback.setEnabled(false);
        this.mSnapAxisFeedback.layerMask = ViewLayerMaskFlags.All & ~ViewLayerMaskFlags.Screenshot;
        this.mSnapAxisFeedback.alpha = 0.5;
        this.mSnapAxisFeedback.renderingGroupId = 1;
        this.mSnapAxisFeedback.color = Color3.FromHexString("#868686");
        this.mSnapAxisFeedback.position = this.mRefPoint;
    }
    clearFeedback() {
        if (this.mSnapAxisFeedback != null) {
            this.mSnapAxisFeedback.dispose();
            this.mSnapAxisFeedback = null;
        }
        if (this.mAxisFeedback != null) {
            for (const tFeedback of this.mAxisFeedback)
                tFeedback.dispose();
            this.mAxisFeedback = null;
        }
        if (this.mRefPointFeedback != null) {
            this.mRefPointFeedback.dispose();
            this.mRefPointFeedback = null;
        }
        if (this.mRefPointFeedbackMat != null) {
            this.mRefPointFeedbackMat.dispose();
            this.mRefPointFeedbackMat = null;
        }
    }
    updateFeedback(pPointer, pCurrentPos, pAxisOnly) {
        if (this.mAxisFeedback != null) {
            for (const tFeedback of this.mAxisFeedback) {
                tFeedback.update(pPointer, pCurrentPos);
            }
        }
        if (!pAxisOnly && this.mRefPointFeedback != null)
            resizeInViewSpace(this.mView, this.mRefPointFeedback, 1, 3);
    }
    snapOnAxis(pPos) {
        if (this.mSnapAxisFeedback == null)
            return (pPos);
        const tLastAxis = this.mCurrentSnapAxis;
        this.mCurrentSnapAxis = null;
        this.mSnapAxisFeedback.setEnabled(false);
        const tResult = SnapLine.Snap(pPos, this.mSnapAxes, this.mView);
        if (tResult != null) {
            this.mCurrentSnapAxis = tResult.line;
            if (tLastAxis != this.mCurrentSnapAxis)
                this.mSnapAxisFeedback.lookAt(this.mCurrentSnapAxis.point2, 0, 0, 0);
            this.mSnapAxisFeedback.setEnabled(true);
            return (tResult.pos);
        }
        return (pPos);
    }
    prepareGrid() {
        this.mGridAxis = new Array(3);
        this.mGridAxis[0] = Math.abs(this.mPickPlane.normal.x) < 1;
        this.mGridAxis[1] = Math.abs(this.mPickPlane.normal.y) < 1;
        this.mGridAxis[2] = Math.abs(this.mPickPlane.normal.z) < 1;
        //update grid size
        this.mSnapGrid.size.copyFrom(this.mApp.settings.snapping.gridSize);
        if (this.mApp.settings.snapping.gridDynamic) {
            this.mSnapGrid.updateSize(this.mRefPoint, this.mView);
        }
        //get grid offset
        this.mSnapGrid.offset = Vector3.Zero();
        const tSnap = this.snapOnGrid(this.mRefPoint);
        if (tSnap != null)
            this.mRefPoint.subtractToRef(tSnap, this.mSnapGrid.offset);
    }
    snapOnGrid(pPos) {
        if (pPos == null)
            return (null);
        if (!this.mApp.settings.snapping.gridEnabled)
            return (pPos);
        //update snapping axis
        this.mSnapGrid.snapXAxis = this.mGridAxis[0];
        this.mSnapGrid.snapYAxis = this.mGridAxis[1];
        this.mSnapGrid.snapZAxis = this.mGridAxis[2];
        if (this.mCurrentSnapAxis != null) {
            this.mSnapGrid.snapXAxis = this.mSnapGrid.snapXAxis && Math.abs(this.mCurrentSnapAxis.direction.x) > 0;
            this.mSnapGrid.snapYAxis = this.mSnapGrid.snapYAxis && Math.abs(this.mCurrentSnapAxis.direction.y) > 0;
            this.mSnapGrid.snapZAxis = this.mSnapGrid.snapZAxis && Math.abs(this.mCurrentSnapAxis.direction.z) > 0;
        }
        return (this.mSnapGrid.snap(pPos, this.mView));
    }
    prepareBoundsSnap() {
        //get snap points of selected elements
        let tBounds;
        let tTransform = null;
        if (this.mApp.model.selection.length === 1) {
            tBounds = this.mApp.model.selection[0].boundingBoxVisible.clone();
            tTransform = this.mApp.model.selection[0].worldTransform;
            this.mBBoxWorld = tBounds.transform(tTransform);
        }
        else {
            tBounds = BoundingBox.FromElements(this.mApp.model.selection, undefined, true);
            this.mBBoxWorld = tBounds;
        }
        this.mBBoxSnapPointsWorld = tBounds.getPoints(tTransform);
        this.mBBoxSnapPoints = new Array();
        for (const tPoint of this.mBBoxSnapPointsWorld)
            this.mBBoxSnapPoints.push(projectPointOnPlane(tPoint, this.mPickPlane));
        //get snapping data of other elements
        this.mElementSnapData = new Array();
        for (const tElement of this.mApp.model.elements) {
            if (tElement.isSelected)
                continue;
            const tBounds = tElement.boundingBoxVisible;
            const tSnapData = {};
            tSnapData.snapLines = new Array();
            tSnapData.snapPoints = new Array();
            tSnapData.boundsRadius = tBounds.getRadius();
            tSnapData.boundsCenter = tBounds.getCenter(tElement.worldTransform);
            const tBPoints = tBounds.getPoints(tElement.worldTransform);
            for (let i = 0; i < 8; ++i) {
                tBPoints[i] = projectPointOnPlane(tBPoints[i], this.mPickPlane);
                tSnapData.snapPoints.push(tBPoints[i]);
            }
            for (const tEdge of BoundingBox.EDGES) {
                const tPoint1 = tBPoints[tEdge[0]];
                const tPoint2 = tBPoints[tEdge[1]];
                if (tPoint1.equalsWithEpsilon(tPoint2, EPSILON))
                    continue;
                const tSnapLine = new SnapLine(tPoint1, tPoint2, true);
                tSnapData.snapLines.push(tSnapLine);
            }
            this.mElementSnapData.push(tSnapData);
        }
    }
    updateBBoxSnapDistance(pPos) {
        const tViewPos = this.mView.projectPoint2(pPos, CoordinatesType.Client);
        if (tViewPos != null) {
            tViewPos.x += 15;
            const tPlanePos = this.getPlanePickPos(tViewPos);
            if (tPlanePos != null)
                this.mBBoxSnapDistance = Vector3.Distance(pPos, tPlanePos);
        }
    }
    snapOnBounds(pPos) {
        if (pPos == null || this.mLastPickPos == null)
            return (null);
        if (!this.mApp.settings.snapping.boundingBoxEnabled || this.mElementSnapData == null ||
            this.mBBoxSnapPoints == null || this.mBBoxSnapPointsWorld == null) {
            this.mBBoxSnapped = false;
            return (pPos);
        }
        this.updateBBoxSnapDistance(pPos);
        const tCurrentTrans = pPos.subtract(this.mLastPickPos);
        let tMinDist = Number.MAX_VALUE;
        let tPoint1 = null;
        let tPoint2 = null;
        let tSnapOnLines = true;
        let tSnappedOnLine = false;
        const updateResult = (pRefPoint, pSnapPoint, pIsLine) => {
            if (pSnapPoint == null)
                return;
            const tDist = Vector3.DistanceSquared(pRefPoint, pSnapPoint);
            if (tDist < tMinDist || (!pIsLine && tSnappedOnLine)) {
                tMinDist = tDist;
                tPoint1 = pRefPoint;
                tPoint2 = pSnapPoint;
                tSnappedOnLine = pIsLine;
            }
        };
        for (let i = 0; i < 8; ++i) {
            let tPoint = this.mBBoxSnapPoints[i];
            tPoint = tPoint.add(this.mTotalTrans);
            tPoint.addInPlace(tCurrentTrans);
            let tPointWorld = this.mBBoxSnapPointsWorld[i];
            tPointWorld = tPointWorld.add(this.mTotalTrans);
            tPointWorld.addInPlace(tCurrentTrans);
            for (const tSnapData of this.mElementSnapData) {
                //check if elment is in snap range
                const tBSphereDistance = Vector3.Distance(tPointWorld, tSnapData.boundsCenter);
                if (tBSphereDistance > tSnapData.boundsRadius + this.mBBoxSnapDistance)
                    continue;
                const tSnappedPoint = SnapPoint.Snap(tPoint, tSnapData.snapPoints, null, this.mBBoxSnapDistance);
                if (tSnappedPoint != null) {
                    tSnapOnLines = false; //prefer points
                    updateResult(tPoint, tSnappedPoint, false);
                }
                if (tSnapOnLines) {
                    const tLineResult = SnapLine.Snap(tPoint, tSnapData.snapLines, null, this.mBBoxSnapDistance);
                    if (tLineResult != null)
                        updateResult(tPoint, tLineResult.pos, true);
                }
            }
        }
        const tNewPos = pPos.clone();
        if (tPoint1 != null && tPoint2 != null) {
            const tDiff = tPoint2.subtract(tPoint1);
            if (this.mCurrentSnapAxis != null) {
                tDiff.multiplyInPlace(this.mCurrentSnapAxis.direction);
            }
            tNewPos.addInPlace(tDiff);
            this.mBBoxSnapped = true;
        }
        else
            this.mBBoxSnapped = false;
        return (tNewPos);
    }
}
